/**
 * Image optimization helper.
 *
 * Routes images through wsrv.nl — a free, fast image proxy that resizes and
 * converts to WebP on the fly. Drops image weight by 10–70×, especially for
 * the customer-assets.emergentagent.com PNGs which are 1–2 MB each.
 *
 * Also supports a `name` option that adds `?filename=<slug>.webp` so the
 * browser, search crawlers, and any "save image" UI see a clean,
 * SEO-friendly filename (e.g. `localizeher-founder-portrait.webp`) instead
 * of leaking the raw ChatGPT-named source filename.
 *
 * Usage:
 *   <img src={optimizeImage(url, { w: 800, name: "founder-portrait" })} />
 *
 * If the URL is empty, falsy, or a data: URL, returns it unchanged.
 */

const PROXY = "https://wsrv.nl/";

function slugify(name) {
  return String(name || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-_]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

/**
 * @param {string} url
 * @param {object} opts
 * @param {number} opts.w        Target width (px). Default 1200.
 * @param {number} [opts.h]      Optional target height.
 * @param {number} [opts.q]      WebP quality 1–100. Default 80.
 * @param {string} [opts.fit]    "cover" | "contain" | "inside" | "outside".
 * @param {string} [opts.name]   SEO-friendly filename slug
 *                                (e.g. "founder-portrait"). The proxy will
 *                                serve the image as `<slug>.webp`.
 * @returns {string}
 */
export function optimizeImage(url, opts = {}) {
  if (!url || typeof url !== "string") return url;
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;
  if (url.startsWith(PROXY)) return url;

  const { w = 1200, h, q = 80, fit = h ? "cover" : undefined, name } = opts;
  const cleaned = url.replace(/^https?:\/\//i, "");
  const params = new URLSearchParams({
    url: cleaned,
    w: String(w),
    output: "webp",
    q: String(q),
  });
  if (h) params.set("h", String(h));
  if (fit) params.set("fit", fit);
  if (name) {
    const slug = slugify(name);
    if (slug) params.set("filename", `${slug}.webp`);
  }
  return `${PROXY}?${params.toString()}`;
}
